import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { Section, VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button } from 'tio-ui/components/buttons';
import { FilterTabs, SkillChip } from 'tio-ui/components/patterns';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import {
  PhStar,
  PhHeart,
  PhShare,
  PhQuotes,
  PhTrendUp,
  PhClock,
  PhBriefcase,
  PhArrowRight,
  PhFunnel,
  PhMagnifyingGlass,
  PhSparkle,
  PhPath,
} from 'ember-phosphor-icons';
import type { SuccessStory } from 'tio-employee/services/career-pathways-api';

const FILTERS = [
  { id: 'all', label: 'All Stories' },
  { id: 'featured', label: 'Featured' },
  { id: 'leadership', label: 'Leadership Paths' },
  { id: 'promotion', label: 'Promotions' },
  { id: 'career-change', label: 'Career Changes' },
];

interface CareerPathwaysSuccessStoriesSignature {
  Args: {
    model: {
      stories: SuccessStory[];
    };
  };
}

export default class CareerPathwaysSuccessStories extends Component<CareerPathwaysSuccessStoriesSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;
  @tracked activeFilter = 'all';
  @tracked likedStories: string[] = [];

  get companyName() {
    if (this.reimaginedData.isDemoMode) {
      return getCurrentDemoClientData().client.name;
    }

    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get stories() {
    return this.args.model.stories;
  }

  setFilter = (filterId: string) => {
    this.activeFilter = filterId;
  };

  toggleLike = (storyId: string) => {
    if (this.likedStories.includes(storyId)) {
      this.likedStories = this.likedStories.filter((id) => id !== storyId);
    } else {
      this.likedStories = [...this.likedStories, storyId];
    }
  };

  isLiked = (storyId: string) => {
    return this.likedStories.includes(storyId);
  };

  get filteredStories() {
    if (this.activeFilter === 'featured') {
      return this.stories.filter((s) => s.featured);
    }
    return this.stories;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{! Header }}
      <div class="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-8 text-white mb-6">
        <div class="flex items-start justify-between">
          <div>
            <div class="flex items-center gap-3 mb-2">
              <PhStar @size={{32}} />
              <Heading @level={{1}} class="text-white text-2xl">Success Stories</Heading>
            </div>
            <Text class="text-amber-100 text-lg max-w-2xl">
              Real employees who transformed their careers using Career Pathways and Tuition
              Assistance. Get inspired by their journeys!
            </Text>
          </div>
          <div class="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
            <div class="text-4xl font-bold">847</div>
            <Text class="text-amber-200">Career Transitions</Text>
            <Text @size="xs" class="text-amber-200">at {{this.companyName}}</Text>
          </div>
        </div>

        {{! Stats }}
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div class="bg-white/10 rounded-lg p-3 text-center">
            <div class="text-2xl font-bold">+38%</div>
            <Text class="text-amber-200 text-sm">Avg Salary Increase</Text>
          </div>
          <div class="bg-white/10 rounded-lg p-3 text-center">
            <div class="text-2xl font-bold">18mo</div>
            <Text class="text-amber-200 text-sm">Avg Transition Time</Text>
          </div>
          <div class="bg-white/10 rounded-lg p-3 text-center">
            <div class="text-2xl font-bold">92%</div>
            <Text class="text-amber-200 text-sm">Still in New Role</Text>
          </div>
          <div class="bg-white/10 rounded-lg p-3 text-center">
            <div class="text-2xl font-bold">4.8/5</div>
            <Text class="text-amber-200 text-sm">Satisfaction Rating</Text>
          </div>
        </div>
      </div>

      {{! Filters }}
      <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div class="flex items-center justify-between">
          <FilterTabs
            @tabs={{FILTERS}}
            @activeId={{this.activeFilter}}
            @onSelect={{this.setFilter}}
            @showCounts={{false}}
            @activeClass="bg-amber-500 text-white"
            @inactiveClass="bg-gray-100 text-gray-600 hover:bg-gray-200"
          />
          <div class="flex gap-2">
            <Button
              @intent="default"
              @appearance="outlined"
              @size="sm"
              @icon={{PhFunnel}}
              @label="More Filters"
            />
            <div class="relative">
              <PhMagnifyingGlass
                @size={{16}}
                class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search stories..."
                class="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>
        </div>
      </div>

      {{! Stories Grid }}
      <div class="space-y-6">
        {{#each this.filteredStories as |story|}}
          <div
            class="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all
              {{if story.featured 'ring-2 ring-amber-200'}}"
          >
            {{#if story.featured}}
              <div
                class="bg-gradient-to-r from-amber-400 to-orange-400 text-white text-sm font-medium px-4 py-2 flex items-center gap-2"
              >
                <PhSparkle @size={{16}} />
                Featured Story
              </div>
            {{/if}}

            <div class="p-6">
              <div class="flex gap-6">
                {{! Left - Avatar and Basic Info }}
                <div class="flex-shrink-0 text-center">
                  <div
                    class="w-20 h-20 rounded-full bg-gradient-to-br
                      {{story.avatarColor}}
                      flex items-center justify-center text-white text-2xl font-bold mb-3"
                  >
                    {{story.initials}}
                  </div>
                  <div class="font-semibold text-gray-900">{{story.employeeName}}</div>
                  <Text @intent="muted" @size="sm">{{story.department}}</Text>
                </div>

                {{! Middle - Journey and Quote }}
                <div class="flex-1">
                  {{! Career Journey }}
                  <div class="flex items-center gap-3 mb-4">
                    <div class="text-center">
                      <Text @intent="muted" @size="xs">From</Text>
                      <div class="font-medium text-gray-700">{{story.fromRole}}</div>
                    </div>
                    <div class="flex-1 flex items-center gap-2">
                      <div class="flex-1 h-0.5 bg-gradient-to-r from-gray-300 to-amber-400"></div>
                      <PhArrowRight @size={{20}} class="text-amber-500" />
                      <div class="flex-1 h-0.5 bg-gradient-to-r from-amber-400 to-green-400"></div>
                    </div>
                    <div class="text-center">
                      <Text @intent="muted" @size="xs">To</Text>
                      <div class="font-semibold text-green-600">{{story.toRole}}</div>
                    </div>
                  </div>

                  {{! Quote }}
                  <div class="relative bg-gray-50 rounded-lg p-4 mb-4">
                    <PhQuotes @size={{24}} class="absolute -top-2 -left-2 text-amber-300" />
                    <Text class="italic text-gray-700 pl-4">"{{story.quote}}"</Text>
                  </div>

                  {{! Skills and Programs }}
                  <div class="flex gap-6">
                    <div>
                      <Text @intent="muted" @size="sm" class="mb-2">Skills Developed:</Text>
                      <div class="flex flex-wrap gap-1">
                        {{#each story.skillsGained as |skill|}}
                          <SkillChip @name={{skill}} @intent="primary" @showLevel={{false}} />
                        {{/each}}
                      </div>
                    </div>
                    <div>
                      <Text @intent="muted" @size="sm" class="mb-2">Programs Completed:</Text>
                      <div class="flex flex-wrap gap-1">
                        {{#each story.programsCompleted as |program|}}
                          <SkillChip @name={{program}} @intent="success" @showLevel={{false}} />
                        {{/each}}
                      </div>
                    </div>
                  </div>
                </div>

                {{! Right - Stats }}
                <div class="flex-shrink-0 w-32 space-y-3">
                  <div class="text-center p-3 bg-green-50 rounded-lg">
                    <PhTrendUp @size={{20}} class="mx-auto text-green-600 mb-1" />
                    <div class="font-bold text-green-600">{{story.salaryIncrease}}</div>
                    <Text @intent="muted" @size="xs">Salary</Text>
                  </div>
                  <div class="text-center p-3 bg-blue-50 rounded-lg">
                    <PhClock @size={{20}} class="mx-auto text-blue-600 mb-1" />
                    <div class="font-bold text-blue-600">{{story.timeframe}}</div>
                    <Text @intent="muted" @size="xs">Timeline</Text>
                  </div>
                </div>
              </div>

              {{! Actions }}
              <div class="flex items-center justify-between pt-4 mt-4 border-t border-gray-100">
                <div class="flex items-center gap-4">
                  <button
                    type="button"
                    {{on "click" (fn this.toggleLike story.id)}}
                    class="flex items-center gap-2 text-sm transition-colors
                      {{if
                        (this.isLiked story.id)
                        'text-pink-500'
                        'text-gray-500 hover:text-pink-500'
                      }}"
                  >
                    <PhHeart @size={{20}} @weight={{if (this.isLiked story.id) "fill" "regular"}} />
                    <span>
                      {{#if (this.isLiked story.id)}}
                        {{story.likes}}
                        + 1
                      {{else}}
                        {{story.likes}}
                      {{/if}}
                    </span>
                  </button>
                  <button
                    type="button"
                    class="flex items-center gap-2 text-sm text-gray-500 hover:text-violet-500"
                  >
                    <PhShare @size={{20}} />
                    <span>Share</span>
                  </button>
                </div>
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  @size="sm"
                  @icon={{PhArrowRight}}
                  @iconPlacement="end"
                  @label="View Full Journey"
                />
              </div>
            </div>
          </div>
        {{/each}}
      </div>

      {{! Load More }}
      <div class="text-center py-8">
        <Button @intent="default" @appearance="outlined">
          Load More Stories
        </Button>
        <Text @intent="muted" @size="sm" class="mt-2">
          Showing 5 of 847 success stories
        </Text>
      </div>

      {{! CTA Section }}
      <Section
        class="bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl p-8 text-white text-center"
      >
        <:body>
          <PhStar @size={{48}} class="mx-auto mb-4 text-amber-300" />
          <Heading @level={{2}} class="text-white text-2xl mb-2">
            Ready to Write Your Own Success Story?
          </Heading>
          <Text class="text-violet-100 mb-6 max-w-xl mx-auto">
            Join hundreds of employees who have transformed their careers using Career Pathways.
            Start your journey today.
          </Text>
          <div class="flex justify-center gap-4">
            <Button
              @intent="default"
              @icon={{PhPath}}
              @label="Start Your Plan"
              class="bg-white text-violet-600 hover:bg-gray-100"
            />
            <Button
              @intent="default"
              @icon={{PhBriefcase}}
              @label="Explore Careers"
              class="bg-white/20 hover:bg-white/30 text-white border-white/30"
            />
          </div>
        </:body>
      </Section>
    </VStack>
  </template>
}
